import React, { useState, useEffect, useMemo, useCallback } from 'react';

import {
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  App,
  Spin,
  Table,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const { Text } = Typography;

const IMPORT_TYPES = [
  'order', 'product', 'inbound', 'return_order',
  'contact_address', 'bundle', 'shelf', 'inventory',
];

export default function ImportTemplatesEdit({
  importTemplateService,
  role = 'admin',
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { id } = useParams();
  const isNew = !id;
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!isNew);
  const [schemaFields, setSchemaFields] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [mappings, setMappings] = useState([{ header: '', field: '' }]);
  const [defaultValues, setDefaultValues] = useState([]);

  const importType = Form.useWatch('import_type', form);

  useEffect(() => {
    if (!isNew) {
      loadTemplate();
    }
  }, [id]);

  // Load schema fields when import_type changes
  useEffect(() => {
    if (importType) {
      loadSchemaFields(importType);
    } else {
      setSchemaFields([]);
    }
  }, [importType]);

  const loadTemplate = async () => {
    setInitialLoading(true);
    try {
      const response = await importTemplateService.get(id);
      if (response.success) {
        const data = response.data;
        form.setFieldsValue({
          name: data.name,
          import_type: data.import_type,
        });
        // Convert column_mappings object to array
        if (data.column_mappings) {
          const mappingArr = Object.entries(data.column_mappings).map(([header, field]) => ({
            header,
            field,
          }));
          setMappings(mappingArr.length > 0 ? mappingArr : [{ header: '', field: '' }]);
        }
        // Convert default_values object to array
        if (data.default_values) {
          const dvArr = Object.entries(data.default_values).map(([field, value]) => ({
            field,
            value: String(value),
          }));
          setDefaultValues(dvArr);
        }
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load import template error:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadSchemaFields = async (type) => {
    setSchemaLoading(true);
    try {
      const response = await importTemplateService.getSchemaFields(type);
      if (response.success) {
        setSchemaFields(response.data || []);
      }
    } catch (error) {
      console.error('Load schema fields error:', error);
    } finally {
      setSchemaLoading(false);
    }
  };

  const fieldOptions = useMemo(() => {
    return schemaFields.map((f) => ({
      label: `${f.label} (${f.key})${f.required ? ' *' : ''}`,
      value: f.key,
    }));
  }, [schemaFields]);

  // Mapping rows
  const addMapping = useCallback(() => {
    setMappings((prev) => [...prev, { header: '', field: '' }]);
  }, []);

  const removeMapping = useCallback((index) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateMapping = useCallback((index, key, value) => {
    setMappings((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  }, []);

  // Default value rows
  const addDefaultValue = useCallback(() => {
    setDefaultValues((prev) => [...prev, { field: '', value: '' }]);
  }, []);

  const removeDefaultValue = useCallback((index) => {
    setDefaultValues((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateDefaultValue = useCallback((index, key, value) => {
    setDefaultValues((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  }, []);

  // 將 form values + 動態 state (mappings/defaultValues) 組合成 payload
  // 驗證失敗時回傳 null
  const buildPayload = (values) => {
    // Validate mappings - at least one valid mapping required
    const validMappings = mappings.filter((m) => m.header.trim() && m.field);
    if (validMappings.length === 0) {
      message.error(t('shared.import_templates.form.at_least_one_mapping'));
      return null;
    }

    // Build column_mappings object
    const column_mappings = {};
    validMappings.forEach((m) => {
      column_mappings[m.header.trim()] = m.field;
    });

    // Build default_values object
    const default_values = {};
    defaultValues
      .filter((d) => d.field && d.value.trim())
      .forEach((d) => {
        default_values[d.field] = d.value.trim();
      });

    return {
      name: values.name,
      import_type: values.import_type,
      column_mappings,
      default_values,
    };
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response
  const submitPayload = async (payload) => {
    return isNew
      ? await importTemplateService.create(payload)
      : await importTemplateService.update(id, payload);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values) => {
    const payload = buildPayload(values);
    if (!payload) return;

    setLoading(true);
    try {
      const response = await submitPayload(payload);

      if (response.success) {
        message.success(t(isNew ? 'common.message.create_success' : 'common.message.update_success'));
        // 導向該筆記錄的 show 頁
        const recordId = response.data?.id || id;
        navigate(`/import_templates/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(isNew ? 'common.error.create_failed' : 'common.error.update_failed'),
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit import template error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單 + 動態 state
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      if (!payload) return;

      setSavingAndAdding(true);
      try {
        const response = await submitPayload(payload);

        if (response.success) {
          message.success(t('common.message.create_success'));
          // 重置 antd form 與動態 state（mappings / defaultValues）
          form.resetFields();
          setMappings([{ header: '', field: '' }]);
          setDefaultValues([]);
          setSchemaFields([]);
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.create_failed'),
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit import template error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const mappingColumns = [
    {
      title: t('shared.import_templates.detail.excel_header'),
      dataIndex: 'header',
      key: 'header',
      render: (_, record, index) => (
        <Input
          value={record.header}
          placeholder={t('shared.import_templates.form.header_placeholder')}
          onChange={(e) => updateMapping(index, 'header', e.target.value)}
        />
      ),
    },
    {
      title: t('shared.import_templates.detail.system_field'),
      dataIndex: 'field',
      key: 'field',
      render: (_, record, index) => (
        <Select
          value={record.field || undefined}
          placeholder={t('shared.import_templates.form.field_placeholder')}
          options={fieldOptions}
          loading={schemaLoading}
          onChange={(val) => updateMapping(index, 'field', val)}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, _record, index) => (
        mappings.length > 1 ? (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeMapping(index)}
          />
        ) : null
      ),
    },
  ];

  const defaultValueColumns = [
    {
      title: t('shared.import_templates.detail.field_name'),
      dataIndex: 'field',
      key: 'field',
      render: (_, record, index) => (
        <Select
          value={record.field || undefined}
          placeholder={t('shared.import_templates.form.field_placeholder')}
          options={fieldOptions}
          loading={schemaLoading}
          onChange={(val) => updateDefaultValue(index, 'field', val)}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('shared.import_templates.detail.default_value'),
      dataIndex: 'value',
      key: 'value',
      render: (_, record, index) => (
        <Input
          value={record.value}
          placeholder={t('shared.import_templates.form.default_value_placeholder')}
          onChange={(e) => updateDefaultValue(index, 'value', e.target.value)}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, _record, index) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeDefaultValue(index)}
        />
      ),
    },
  ];

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={['data', 'data.import_templates', { label: isNew ? t('common.button.create') : t('common.button.edit') }]}
      />

      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label={t('shared.import_templates.form.name')}
            name="name"
            rules={[{ required: true, message: t('shared.import_templates.form.name_required') }]}
          >
            <Input placeholder={t('shared.import_templates.form.name_placeholder')} />
          </Form.Item>

          <Form.Item
            label={t('shared.import_templates.form.import_type')}
            name="import_type"
            rules={[{ required: true, message: t('shared.import_templates.form.import_type_required') }]}
          >
            <Select
              placeholder={t('shared.import_templates.form.import_type_placeholder')}
              disabled={!isNew}
              options={IMPORT_TYPES.map((type) => ({
                label: t(`common.import_type.${type}`),
                value: type,
              }))}
            />
          </Form.Item>

          <FloatingActionBarSpacer />
        </Form>
      </Card>

      {/* Column Mappings */}
      <Card
        title={t('shared.import_templates.detail.column_mappings')}
        extra={
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addMapping}
            disabled={!importType}
          >
            {t('common.button.add')}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {!importType ? (
          <Text type="secondary">{t('shared.import_templates.form.select_type_first')}</Text>
        ) : (
          <Table
            columns={mappingColumns}
            dataSource={mappings.map((m, i) => ({ ...m, key: i }))}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Default Values */}
      <Card
        title={t('shared.import_templates.detail.default_values')}
        extra={
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addDefaultValue}
            disabled={!importType}
          >
            {t('common.button.add')}
          </Button>
        }
      >
        {!importType ? (
          <Text type="secondary">{t('shared.import_templates.form.select_type_first')}</Text>
        ) : defaultValues.length === 0 ? (
          <Text type="secondary">{t('shared.import_templates.form.no_default_values')}</Text>
        ) : (
          <Table
            columns={defaultValueColumns}
            dataSource={defaultValues.map((d, i) => ({ ...d, key: i }))}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
}
